/**
 * Ethics Entity - RTK Query API
 * API endpoints for Ethics & Whistleblowing domain using RTK Query with Django REST Framework
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';
import { archiveArgs } from '../../../shared/api/archiveArgs';

const normalizeListResponse = (response) => ({
  data: response?.results || response || [],
  total: response?.count || response?.length || 0,
});

// ETH-19: these four query builders previously sent filter param keys that
// do not exist on the corresponding ViewSet.filterset_fields
// (channel/declarant/gift_type/ethics_case), so DjangoFilterBackend
// silently ignored them -- filters that looked wired but matched nothing.
// Extracted as named functions (same pattern as normalizeListResponse) so
// the corrected query params can be asserted directly in a unit test.
const buildWhistleblowerReportsQuery = ({ page = 1, limit = 20, status = '' } = {}) => ({
  url: '/api/v1/ethics/whistleblower/',
  params: {
    page,
    page_size: limit,
    status: status || undefined,
  },
});

const buildConflictsOfInterestQuery = ({
  page = 1,
  limit = 20,
  status = '',
  declarant = '',
} = {}) => ({
  url: '/api/v1/ethics/coi/',
  params: {
    page,
    page_size: limit,
    status: status || undefined,
    disclosed_by: declarant || undefined,
  },
});

const buildGiftDeclarationsQuery = ({ page = 1, limit = 20, status = '', type = '' } = {}) => ({
  url: '/api/v1/ethics/gifts/',
  params: {
    page,
    page_size: limit,
    status: status || undefined,
    declaration_type: type || undefined,
  },
});

const buildInvestigationsQuery = ({ page = 1, limit = 20, status = '', caseId = '' } = {}) => ({
  url: '/api/v1/ethics/investigations/',
  params: {
    page,
    page_size: limit,
    status: status || undefined,
    case: caseId || undefined,
  },
});

// ETH-10: getOpenCasesCount queried `/api/v1/ethics/cases/?status=open`, but
// 'open' is not one of EthicsCase.STATUS_CHOICES (received/triaged/
// under_investigation/pending_action/resolved/closed/dismissed) --
// DjangoFilterBackend silently ignored it and the count always came back as
// the total case count, not the open count. Reimplemented against the
// dashboard endpoint's `cases_by_status` breakdown, summing the non-terminal
// buckets.
const OPEN_CASE_STATUSES = ['received', 'triaged', 'under_investigation', 'pending_action'];

const transformOpenCasesCount = (response) => {
  const byStatus = response?.cases_by_status || {};
  return OPEN_CASE_STATUSES.reduce((total, key) => total + (byStatus[key] || 0), 0);
};

// ETH-11: these two query builders hit `available_transitions/` and
// `transition_history/`, but WorkflowViewMixin (apps/shared/workflow/mixins.py)
// registers its actions at `transitions/available/` and `transitions/history/`
// (url_path='transitions/available' / 'transitions/history') -- the old paths
// 404. Nothing in the UI currently calls either hook (grepped src), so this
// is dead-code-correctness only; logged as DEFERRED in the ledger that the
// case workflow (triage/resolve/transition) still has no UI entry point.
const buildEthicsCaseAvailableTransitionsUrl = (id) =>
  `/api/v1/ethics/cases/${id}/transitions/available/`;

const buildEthicsCaseTransitionHistoryUrl = (id) =>
  `/api/v1/ethics/cases/${id}/transitions/history/`;

// ETH-14 (D7): the code-of-conduct document is streamed by a tenant-scoped
// download action -- never a storage URL. Fetched as a blob through the
// authenticated base query, then saved via a temporary object URL.
const filenameFromDisposition = (header) => {
  if (!header) return null;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) return decodeURIComponent(utf8[1]);
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain?.[1] || null;
};

const saveBlob = (blob, filename) => {
  if (typeof window === 'undefined' || !window.URL?.createObjectURL) return;
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
};

// ETH-15: only send the score when the user typed one (it is optional and
// self-reported); the backend validates 0-100.
const buildCompleteEnrollmentBody = ({ score } = {}) =>
  score === undefined || score === null || score === '' ? {} : { score: Number(score) };

// ETH-14: create a code-of-conduct version as multipart (with an optional
// document). Content-Type is left to the browser so the boundary is set.
const buildCodeOfConductFormData = (data) => {
  const form = new FormData();
  Object.entries(data || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    form.append(key, typeof value === 'boolean' ? String(value) : value);
  });
  return form;
};

export const ethicsApiTestUtils = {
  normalizeListResponse,
  buildWhistleblowerReportsQuery,
  buildConflictsOfInterestQuery,
  buildGiftDeclarationsQuery,
  buildInvestigationsQuery,
  transformOpenCasesCount,
  buildEthicsCaseAvailableTransitionsUrl,
  buildEthicsCaseTransitionHistoryUrl,
  buildCompleteEnrollmentBody,
  buildCodeOfConductFormData,
  filenameFromDisposition,
};

export const ethicsApi = createApi({
  reducerPath: 'ethicsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'EthicsCase',
    'EthicsInvestigation',
    'EthicsViolation',
    'WhistleblowerReport',
    'ConflictOfInterest',
    'GiftDeclaration',
    'EthicsPolicy',
    'EthicsTraining',
    'EthicsStats',
    'CultureSurvey',
    'SurveyResponse',
    'WorkflowTransition',
    'CodeOfConduct',
    'CodeAcknowledgement',
    'TrainingEnrollment',
  ],
  endpoints: (builder) => ({
    // =========================================================================
    // ETHICS CASES
    // =========================================================================

    /**
     * Get paginated list of ethics cases
     */
    getEthicsCases: builder.query({
      query: ({
        page = 1,
        limit = 20,
        status = '',
        category = '',
        priority = '',
        affected_unit = '',
        owning_unit = '',
      } = {}) => ({
        url: '/api/v1/ethics/cases/',
        params: {
          page,
          page_size: limit,
          status: status || undefined,
          category: category || undefined,
          priority: priority || undefined,
          affected_unit: affected_unit || undefined,
          owning_unit: owning_unit || undefined,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'EthicsCase', id })),
              { type: 'EthicsCase', id: 'LIST' },
            ]
          : [{ type: 'EthicsCase', id: 'LIST' }],
    }),

    /**
     * Get single ethics case by ID
     */
    getEthicsCaseById: builder.query({
      query: (id) => `/api/v1/ethics/cases/${id}/`,
      providesTags: (result, error, id) => [{ type: 'EthicsCase', id }],
    }),

    /**
     * Create new ethics case
     */
    createEthicsCase: builder.mutation({
      query: (data) => ({
        url: '/api/v1/ethics/cases/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'EthicsCase', id: 'LIST' }, { type: 'EthicsStats' }],
    }),

    /**
     * Update ethics case
     */
    updateEthicsCase: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/ethics/cases/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'EthicsCase', id },
        { type: 'EthicsCase', id: 'LIST' },
        { type: 'EthicsStats' },
      ],
    }),

    // =========================================================================
    // WHISTLEBLOWER REPORTS
    // =========================================================================

    /**
     * Get whistleblower reports (anonymized list)
     */
    getWhistleblowerReports: builder.query({
      query: buildWhistleblowerReportsQuery,
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'WhistleblowerReport', id })),
              { type: 'WhistleblowerReport', id: 'LIST' },
            ]
          : [{ type: 'WhistleblowerReport', id: 'LIST' }],
    }),

    /**
     * Get whistleblower report by ID
     */
    getWhistleblowerReportById: builder.query({
      query: (id) => `/api/v1/ethics/whistleblower/${id}/`,
      providesTags: (result, error, id) => [{ type: 'WhistleblowerReport', id }],
    }),

    /**
     * Create a whistleblower report via the authenticated staff-facing
     * ViewSet endpoint. Requires an authenticated session with ethics
     * permissions -- NOT the anonymous public intake channel. For the real
     * unauthenticated/anonymous submission flow (ETH-04) use
     * submitAnonymousWhistleblowerReport below, which targets the dedicated
     * AllowAny endpoint at /api/v1/ethics/whistleblower/submit/.
     */
    submitWhistleblowerReport: builder.mutation({
      query: (data) => ({
        url: '/api/v1/ethics/whistleblower/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'WhistleblowerReport', id: 'LIST' }, { type: 'EthicsStats' }],
    }),

    /**
     * ETH-04: submit an anonymous whistleblower report via the public,
     * unauthenticated intake endpoint (AllowAny, rate-limited, never linked
     * to an authenticated user or logged to the audit trail). Body:
     * { tenant_slug, category, description, supporting_details?, contact? }.
     * Response contains only { claim_code } -- the caller's one-way
     * reference for status checks; nothing else identifying is returned.
     */
    submitAnonymousWhistleblowerReport: builder.mutation({
      query: (data) => ({
        url: '/api/v1/ethics/whistleblower/submit/',
        method: 'POST',
        body: data,
      }),
    }),

    /**
     * ETH-04: check an anonymous whistleblower report's status using the
     * claim code issued at submission. Public, unauthenticated, AllowAny.
     * Returns only non-identifying fields: category, status, created_at.
     */
    checkWhistleblowerStatus: builder.query({
      query: (claimCode) => ({
        url: '/api/v1/ethics/whistleblower/status/',
        params: { code: claimCode },
      }),
    }),

    /**
     * Update whistleblower report status
     */
    updateWhistleblowerReport: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/ethics/whistleblower/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'WhistleblowerReport', id },
        { type: 'WhistleblowerReport', id: 'LIST' },
      ],
    }),

    // =========================================================================
    // CONFLICTS OF INTEREST
    // =========================================================================

    /**
     * Get conflicts of interest declarations
     */
    getConflictsOfInterest: builder.query({
      query: buildConflictsOfInterestQuery,
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'ConflictOfInterest', id })),
              { type: 'ConflictOfInterest', id: 'LIST' },
            ]
          : [{ type: 'ConflictOfInterest', id: 'LIST' }],
    }),

    /**
     * Get conflict of interest by ID
     */
    getConflictOfInterestById: builder.query({
      query: (id) => `/api/v1/ethics/coi/${encodeURIComponent(id)}/`,
      providesTags: (result, error, id) => [{ type: 'ConflictOfInterest', id }],
    }),

    /**
     * Submit conflict of interest declaration
     */
    submitConflictOfInterest: builder.mutation({
      query: (data) => ({
        url: '/api/v1/ethics/coi/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'ConflictOfInterest', id: 'LIST' }],
    }),

    /**
     * Update conflict of interest
     */
    updateConflictOfInterest: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/ethics/coi/${encodeURIComponent(id)}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ConflictOfInterest', id },
        { type: 'ConflictOfInterest', id: 'LIST' },
      ],
    }),

    /**
     * Review a conflict of interest declaration (approve/mitigated/rejected)
     */
    reviewConflictOfInterest: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/ethics/coi/${id}/review/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ConflictOfInterest', id },
        { type: 'ConflictOfInterest', id: 'LIST' },
      ],
    }),

    // Approval routing C2 (Task 6): a COI sent back for changes (status pending, approval
    // returned) is edited, then resubmitted -- or withdrawn -- by its discloser or an admin.
    resubmitConflictOfInterest: builder.mutation({
      query: ({ id, approvers }) => ({
        url: `/api/v1/ethics/coi/${encodeURIComponent(id)}/resubmit/`,
        method: 'POST',
        body: approvers ? { approvers } : {},
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ConflictOfInterest', id },
        { type: 'ConflictOfInterest', id: 'LIST' },
      ],
    }),

    withdrawConflictOfInterest: builder.mutation({
      query: (id) => ({
        url: `/api/v1/ethics/coi/${encodeURIComponent(id)}/withdraw/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ConflictOfInterest', id },
        { type: 'ConflictOfInterest', id: 'LIST' },
      ],
    }),

    // =========================================================================
    // GIFT DECLARATIONS
    // =========================================================================

    /** One declaration's DETAIL record: carries approval_request_id/approval_status (R19). */
    getGiftDeclarationById: builder.query({
      query: (id) => `/api/v1/ethics/gifts/${encodeURIComponent(id)}/`,
      providesTags: (result, error, id) => [{ type: 'GiftDeclaration', id }],
    }),

    /** Edit a declaration sent back for changes (correction_requested). */
    updateGiftDeclaration: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/ethics/gifts/${encodeURIComponent(id)}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'GiftDeclaration', id },
        { type: 'GiftDeclaration', id: 'LIST' },
      ],
    }),

    // Approval routing C2 (Task 5): declarer or admin, from correction_requested only.
    resubmitGiftDeclaration: builder.mutation({
      query: ({ id, approvers }) => ({
        url: `/api/v1/ethics/gifts/${encodeURIComponent(id)}/resubmit/`,
        method: 'POST',
        body: approvers ? { approvers } : {},
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'GiftDeclaration', id },
        { type: 'GiftDeclaration', id: 'LIST' },
      ],
    }),

    withdrawGiftDeclaration: builder.mutation({
      query: (id) => ({
        url: `/api/v1/ethics/gifts/${encodeURIComponent(id)}/withdraw/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'GiftDeclaration', id },
        { type: 'GiftDeclaration', id: 'LIST' },
      ],
    }),

    /**
     * Get gift declarations
     */
    getGiftDeclarations: builder.query({
      query: buildGiftDeclarationsQuery,
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'GiftDeclaration', id })),
              { type: 'GiftDeclaration', id: 'LIST' },
            ]
          : [{ type: 'GiftDeclaration', id: 'LIST' }],
    }),

    /**
     * Submit gift declaration
     */
    submitGiftDeclaration: builder.mutation({
      query: (data) => ({
        url: '/api/v1/ethics/gifts/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'GiftDeclaration', id: 'LIST' }],
    }),

    /**
     * ETH-13: the caller's own declarations (self-service).
     */
    getMyGiftDeclarations: builder.query({
      query: () => '/api/v1/ethics/gifts/my_declarations/',
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'GiftDeclaration', id: 'LIST' }],
    }),

    /**
     * ETH-13: reviewer queue (ethics:write); excludes nothing server-side,
     * the UI hides the reviewer's own rows' actions (backend returns 403).
     */
    getPendingGiftApprovals: builder.query({
      query: () => '/api/v1/ethics/gifts/pending_approval/',
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'GiftDeclaration', id: 'LIST' }],
    }),

    approveGiftDeclaration: builder.mutation({
      query: ({ id, approval_notes = '' }) => ({
        url: `/api/v1/ethics/gifts/${id}/approve/`,
        method: 'POST',
        body: { approval_notes },
      }),
      invalidatesTags: [{ type: 'GiftDeclaration', id: 'LIST' }],
    }),

    rejectGiftDeclaration: builder.mutation({
      query: ({ id, approval_notes = '' }) => ({
        url: `/api/v1/ethics/gifts/${id}/reject/`,
        method: 'POST',
        body: { approval_notes },
      }),
      invalidatesTags: [{ type: 'GiftDeclaration', id: 'LIST' }],
    }),

    // =========================================================================
    // INVESTIGATIONS
    // =========================================================================

    /**
     * Get ethics investigations
     */
    getInvestigations: builder.query({
      query: buildInvestigationsQuery,
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'EthicsInvestigation', id })),
              { type: 'EthicsInvestigation', id: 'LIST' },
            ]
          : [{ type: 'EthicsInvestigation', id: 'LIST' }],
    }),

    /**
     * Get investigation by ID
     */
    getInvestigationById: builder.query({
      query: (id) => `/api/v1/ethics/investigations/${id}/`,
      providesTags: (result, error, id) => [{ type: 'EthicsInvestigation', id }],
    }),

    /**
     * Start investigation
     */
    startInvestigation: builder.mutation({
      query: (data) => ({
        url: '/api/v1/ethics/investigations/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'EthicsInvestigation', id: 'LIST' },
        { type: 'EthicsCase', id: 'LIST' },
      ],
    }),

    /**
     * Update investigation
     */
    updateInvestigation: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/ethics/investigations/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'EthicsInvestigation', id },
        { type: 'EthicsInvestigation', id: 'LIST' },
      ],
    }),

    // =========================================================================
    // ETHICS TRAINING
    // =========================================================================

    /**
     * Training catalogue. ETH-15: this hits the course list, which has no
     * `user` filter -- the old `user` param was silently ignored, so it is
     * gone. Per-user progress lives under enrollments (getMyEnrollments).
     */
    getTrainingRecords: builder.query({
      query: ({ page = 1, limit = 20, status = '' } = {}) => ({
        url: '/api/v1/ethics/training/',
        params: {
          page,
          page_size: limit,
          status: status || undefined,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'EthicsTraining', id: 'LIST' }],
    }),

    createTraining: builder.mutation({
      query: (data) => ({ url: '/api/v1/ethics/training/', method: 'POST', body: data }),
      invalidatesTags: [{ type: 'EthicsTraining', id: 'LIST' }],
    }),

    deleteTraining: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/ethics/training/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: [{ type: 'EthicsTraining', id: 'LIST' }],
    }),

    enrollUsersInTraining: builder.mutation({
      query: ({ id, user_ids, due_date }) => ({
        url: `/api/v1/ethics/training/${id}/enroll_users/`,
        method: 'POST',
        body: due_date ? { user_ids, due_date } : { user_ids },
      }),
      invalidatesTags: [
        { type: 'EthicsTraining', id: 'LIST' },
        { type: 'TrainingEnrollment', id: 'LIST' },
      ],
    }),

    getMyEnrollments: builder.query({
      query: () => '/api/v1/ethics/enrollments/my_enrollments/',
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'TrainingEnrollment', id: 'LIST' }],
    }),

    getPendingConfirmationEnrollments: builder.query({
      query: () => '/api/v1/ethics/enrollments/pending_confirmation/',
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'TrainingEnrollment', id: 'LIST' }],
    }),

    getOverdueEnrollments: builder.query({
      query: () => '/api/v1/ethics/enrollments/overdue/',
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'TrainingEnrollment', id: 'LIST' }],
    }),

    startEnrollment: builder.mutation({
      query: (id) => ({
        url: `/api/v1/ethics/enrollments/${id}/start/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: [{ type: 'TrainingEnrollment', id: 'LIST' }],
    }),

    completeEnrollment: builder.mutation({
      query: ({ id, score }) => ({
        url: `/api/v1/ethics/enrollments/${id}/complete/`,
        method: 'POST',
        body: buildCompleteEnrollmentBody({ score }),
      }),
      invalidatesTags: [{ type: 'TrainingEnrollment', id: 'LIST' }],
    }),

    /**
     * ETH-15 (D10): manager decision. outcome: passed | failed | returned.
     */
    confirmEnrollment: builder.mutation({
      query: ({ id, outcome, score, notes = '' }) => ({
        url: `/api/v1/ethics/enrollments/${id}/confirm/`,
        method: 'POST',
        body: {
          outcome,
          notes,
          ...(score === undefined || score === null || score === ''
            ? {}
            : { score: Number(score) }),
        },
      }),
      invalidatesTags: [
        { type: 'TrainingEnrollment', id: 'LIST' },
        { type: 'EthicsTraining', id: 'LIST' },
      ],
    }),

    // =========================================================================
    // CODE OF CONDUCT (ETH-14)
    // =========================================================================
    getCodesOfConduct: builder.query({
      query: ({ page = 1, limit = 20 } = {}) => ({
        url: '/api/v1/ethics/code-of-conduct/',
        params: { page, page_size: limit },
      }),
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'CodeOfConduct', id: 'LIST' }],
    }),

    getCurrentCodeOfConduct: builder.query({
      query: () => '/api/v1/ethics/code-of-conduct/current/',
      providesTags: [{ type: 'CodeOfConduct', id: 'CURRENT' }],
    }),

    createCodeOfConduct: builder.mutation({
      query: (data) => ({
        url: '/api/v1/ethics/code-of-conduct/',
        method: 'POST',
        body: buildCodeOfConductFormData(data),
      }),
      invalidatesTags: [{ type: 'CodeOfConduct', id: 'LIST' }],
    }),

    /** One version's DETAIL record (approval fields, R19); drafts are reviewer-only. */
    getCodeOfConductById: builder.query({
      query: (id) => `/api/v1/ethics/code-of-conduct/${encodeURIComponent(id)}/`,
      providesTags: (result, error, id) => [{ type: 'CodeOfConduct', id }],
    }),

    // Approval routing C2 (Task 7): a draft goes live only through approval; activate
    // answers 409 "Submit it for approval." (ruling C2-R20).
    submitCodeOfConduct: builder.mutation({
      query: ({ id, approvers }) => ({
        url: `/api/v1/ethics/code-of-conduct/${encodeURIComponent(id)}/submit/`,
        method: 'POST',
        body: approvers ? { approvers } : {},
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'CodeOfConduct', id },
        { type: 'CodeOfConduct', id: 'LIST' },
      ],
    }),

    activateCodeOfConduct: builder.mutation({
      query: (id) => ({
        url: `/api/v1/ethics/code-of-conduct/${id}/activate/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: [
        { type: 'CodeOfConduct', id: 'LIST' },
        { type: 'CodeOfConduct', id: 'CURRENT' },
        { type: 'CodeAcknowledgement', id: 'LIST' },
      ],
    }),

    getCodeOfConductAcknowledgements: builder.query({
      query: (id) => `/api/v1/ethics/code-of-conduct/${id}/acknowledgements/`,
      providesTags: [{ type: 'CodeAcknowledgement', id: 'LIST' }],
    }),

    downloadCodeOfConductDocument: builder.mutation({
      async queryFn({ id, filename }, _api, _extra, baseQuery) {
        const result = await baseQuery({
          url: `/api/v1/ethics/code-of-conduct/${id}/download/`,
          responseHandler: (response) => (response.ok ? response.blob() : response.json()),
        });
        if (result.error) return { error: result.error };
        const name =
          filenameFromDisposition(result.meta?.response?.headers?.get('content-disposition')) ||
          filename ||
          'code-of-conduct';
        saveBlob(result.data, name);
        return { data: { filename: name } };
      },
    }),

    getPendingAcknowledgements: builder.query({
      query: () => '/api/v1/ethics/acknowledgements/pending/',
      providesTags: [{ type: 'CodeAcknowledgement', id: 'LIST' }],
    }),

    getMyAcknowledgements: builder.query({
      query: () => '/api/v1/ethics/acknowledgements/my_acknowledgements/',
      providesTags: [{ type: 'CodeAcknowledgement', id: 'LIST' }],
    }),

    acknowledgeCodeOfConduct: builder.mutation({
      query: ({ code_of_conduct, confirmation_text }) => ({
        url: '/api/v1/ethics/acknowledgements/',
        method: 'POST',
        body: { code_of_conduct, confirmation_text },
      }),
      invalidatesTags: [
        { type: 'CodeAcknowledgement', id: 'LIST' },
        { type: 'CodeOfConduct', id: 'LIST' },
      ],
    }),

    // =========================================================================
    // STATISTICS
    // =========================================================================

    /**
     * Get ethics dashboard statistics
     */
    getEthicsStats: builder.query({
      query: () => '/api/v1/ethics/dashboard/',
      providesTags: [{ type: 'EthicsStats' }],
    }),

    /**
     * Get open cases count
     */
    getOpenCasesCount: builder.query({
      query: () => '/api/v1/ethics/dashboard/',
      transformResponse: transformOpenCasesCount,
      providesTags: [{ type: 'EthicsCase', id: 'COUNT' }],
    }),

    // =========================================================================
    // CULTURE SURVEYS
    // =========================================================================
    getCultureSurveys: builder.query({
      query: ({ page = 1, limit = 20, status = '' } = {}) => ({
        url: '/api/v1/ethics/culture-surveys/',
        params: { page, page_size: limit, status: status || undefined },
      }),
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'CultureSurvey', id: 'LIST' }],
    }),

    getCultureSurveyById: builder.query({
      query: (id) => `/api/v1/ethics/culture-surveys/${id}/`,
      providesTags: (result, error, id) => [{ type: 'CultureSurvey', id }],
    }),

    createCultureSurvey: builder.mutation({
      query: (data) => ({
        url: '/api/v1/ethics/culture-surveys/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'CultureSurvey', id: 'LIST' }],
    }),

    launchCultureSurvey: builder.mutation({
      query: (id) => ({
        url: `/api/v1/ethics/culture-surveys/${id}/launch/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'CultureSurvey', id: 'LIST' },
        { type: 'CultureSurvey', id },
      ],
    }),

    closeCultureSurvey: builder.mutation({
      query: (id) => ({
        url: `/api/v1/ethics/culture-surveys/${id}/close/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'CultureSurvey', id: 'LIST' },
        { type: 'CultureSurvey', id },
      ],
    }),

    // ETH-18: question editor (reviewers, draft surveys only).
    createSurveyQuestion: builder.mutation({
      query: (data) => ({ url: '/api/v1/ethics/survey-questions/', method: 'POST', body: data }),
      invalidatesTags: (result, error, { survey }) => [{ type: 'CultureSurvey', id: survey }],
    }),

    updateSurveyQuestion: builder.mutation({
      // `survey` only drives cache invalidation; a question never changes survey.
      query: ({ id, survey: _survey, ...data }) => ({
        url: `/api/v1/ethics/survey-questions/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { survey }) => [{ type: 'CultureSurvey', id: survey }],
    }),

    deleteSurveyQuestion: builder.mutation({
      query: ({ id }) => ({ url: `/api/v1/ethics/survey-questions/${id}/`, method: 'DELETE' }),
      invalidatesTags: (result, error, { survey }) => [{ type: 'CultureSurvey', id: survey }],
    }),

    getCultureSurveyResults: builder.query({
      query: (id) => `/api/v1/ethics/culture-surveys/${id}/results/`,
      providesTags: (result, error, id) => [{ type: 'CultureSurvey', id }],
    }),

    // =========================================================================
    // SURVEY RESPONSES
    // =========================================================================
    /**
     * ETH-18: { survey, answers: [{ question, rating_value | choice_value |
     * text_value }], completion_time_seconds? }. Anonymous surveys store no
     * user; the response body only confirms submission.
     */
    submitSurveyResponse: builder.mutation({
      query: (data) => ({
        url: '/api/v1/ethics/survey-responses/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { survey }) => [
        { type: 'CultureSurvey', id: 'LIST' },
        { type: 'CultureSurvey', id: survey },
      ],
    }),

    // =========================================================================
    // Workflow Transitions (WorkflowEngine)
    // POST /api/v1/ethics/cases/{id}/transition/
    // GET  /api/v1/ethics/cases/{id}/transitions/available/
    // GET  /api/v1/ethics/cases/{id}/transitions/history/
    // States (ISO 37002:2021): received / triaged / under_investigation / resolved / closed
    // =========================================================================

    /**
     * Transition an ethics case to a new workflow state.
     * Body: { to_state, comment?, evidence_ref? }
     */
    transitionEthicsCase: builder.mutation({
      query: ({ id, to_state, comment = '', evidence_ref = '' }) => ({
        url: `/api/v1/ethics/cases/${id}/transition/`,
        method: 'POST',
        body: { to_state, comment, evidence_ref },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'EthicsCase', id },
        { type: 'EthicsCase', id: 'LIST' },
        { type: 'WorkflowTransition', id },
        { type: 'EthicsStats' },
      ],
    }),

    /**
     * Get available transitions for an ethics case from its current state.
     */
    getEthicsCaseAvailableTransitions: builder.query({
      query: (id) => ({ url: buildEthicsCaseAvailableTransitionsUrl(id) }),
      providesTags: (result, error, id) => [{ type: 'WorkflowTransition', id }],
    }),

    /**
     * Get full transition history log for an ethics case.
     */
    getEthicsCaseTransitionHistory: builder.query({
      query: (id) => ({ url: buildEthicsCaseTransitionHistoryUrl(id) }),
      providesTags: (result, error, id) => [{ type: 'WorkflowTransition', id }],
    }),
  }),
});

// Export hooks for components
export const {
  // Ethics Cases
  useGetEthicsCasesQuery,
  useGetEthicsCaseByIdQuery,
  useCreateEthicsCaseMutation,
  useUpdateEthicsCaseMutation,
  // Whistleblower
  useGetWhistleblowerReportsQuery,
  useGetWhistleblowerReportByIdQuery,
  useSubmitWhistleblowerReportMutation,
  useSubmitAnonymousWhistleblowerReportMutation,
  useCheckWhistleblowerStatusQuery,
  useLazyCheckWhistleblowerStatusQuery,
  useUpdateWhistleblowerReportMutation,
  // Conflicts of Interest
  useGetConflictsOfInterestQuery,
  useGetConflictOfInterestByIdQuery,
  useSubmitConflictOfInterestMutation,
  useUpdateConflictOfInterestMutation,
  useReviewConflictOfInterestMutation,
  useResubmitConflictOfInterestMutation,
  useWithdrawConflictOfInterestMutation,
  // Gifts
  useGetGiftDeclarationsQuery,
  useSubmitGiftDeclarationMutation,
  useGetMyGiftDeclarationsQuery,
  useGetPendingGiftApprovalsQuery,
  useApproveGiftDeclarationMutation,
  useRejectGiftDeclarationMutation,
  useGetGiftDeclarationByIdQuery,
  useUpdateGiftDeclarationMutation,
  useResubmitGiftDeclarationMutation,
  useWithdrawGiftDeclarationMutation,
  // Investigations
  useGetInvestigationsQuery,
  useGetInvestigationByIdQuery,
  useStartInvestigationMutation,
  useUpdateInvestigationMutation,
  // Training
  useGetTrainingRecordsQuery,
  useCreateTrainingMutation,
  useDeleteTrainingMutation,
  useEnrollUsersInTrainingMutation,
  useGetMyEnrollmentsQuery,
  useGetPendingConfirmationEnrollmentsQuery,
  useGetOverdueEnrollmentsQuery,
  useStartEnrollmentMutation,
  useCompleteEnrollmentMutation,
  useConfirmEnrollmentMutation,
  // Code of conduct
  useGetCodesOfConductQuery,
  useGetCurrentCodeOfConductQuery,
  useCreateCodeOfConductMutation,
  useActivateCodeOfConductMutation,
  useGetCodeOfConductByIdQuery,
  useSubmitCodeOfConductMutation,
  useGetCodeOfConductAcknowledgementsQuery,
  useDownloadCodeOfConductDocumentMutation,
  useGetPendingAcknowledgementsQuery,
  useGetMyAcknowledgementsQuery,
  useAcknowledgeCodeOfConductMutation,
  // Stats
  useGetEthicsStatsQuery,
  useGetOpenCasesCountQuery,
  // Culture Surveys
  useGetCultureSurveysQuery,
  useGetCultureSurveyByIdQuery,
  useCreateCultureSurveyMutation,
  useLaunchCultureSurveyMutation,
  useCloseCultureSurveyMutation,
  useGetCultureSurveyResultsQuery,
  useCreateSurveyQuestionMutation,
  useUpdateSurveyQuestionMutation,
  useDeleteSurveyQuestionMutation,
  // Survey Responses
  useSubmitSurveyResponseMutation,
  // Workflow
  useTransitionEthicsCaseMutation,
  useGetEthicsCaseAvailableTransitionsQuery,
  useGetEthicsCaseTransitionHistoryQuery,
} = ethicsApi;
